import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiMail, FiEdit, FiRefreshCw, FiSettings, FiChevronDown } from 'react-icons/fi';
import EmailSidebar from './components/EmailSidebar';
import EmailList from './components/EmailList';
import EmailReadingPane from './components/EmailReadingPane';
import ComposePanel from './components/ComposePanel';
import AccountDropdown from './components/AccountDropdown';
import EmailService from '../../services/EmailService';

const FOLDER_LABEL_ID = {
  INBOX: 'INBOX',
  SENT: 'SENT',
  DRAFT: null,
  TRASH: 'TRASH',
};

const EmailInboxLayout = ({ accounts, onAddAccount, onDisconnect }) => {
  const [activeSelection, setActiveSelection] = useState(
    accounts.length > 0 ? { accountId: accounts[0].id, folder: 'INBOX' } : null
  );
  const [activeTab, setActiveTab] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [composePreFill, setComposePreFill] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [imagePreviews, setImagePreviews] = useState({});

  const activeSelectionRef = useRef(activeSelection);
  activeSelectionRef.current = activeSelection;


  const getActiveAccount = useCallback(() => {
    if (!activeSelection) return null;
    return accounts.find((a) => a.id === activeSelection.accountId) || null;
  }, [accounts, activeSelection]);

  const getAttachmentKey = (a) => a.attachmentId || a.attachmentPath;

  useEffect(() => {
    const account = getActiveAccount();
    const images = (selectedEmail?.attachments || []).filter((a) => (a.mimeType || '').startsWith('image/'));
    setImagePreviews({});
    if (!account || images.length === 0) return undefined;

    let cancelled = false;
    const createdUrls = [];

    (async () => {
      for (const att of images) {
        try {
          const blob = account.type === 'gmail'
            ? await EmailService.fetchGmailAttachmentBlob(account.token, selectedEmail.id, att)
            : account.type === 'zoho'
              ? await EmailService.fetchZohoAttachmentBlob(account.token, selectedEmail.id, att)
              : null;
          if (!blob || cancelled) continue;
          const url = URL.createObjectURL(blob);
          createdUrls.push(url);
          setImagePreviews((prev) => ({ ...prev, [getAttachmentKey(att)]: url }));
        } catch {
          // preview fetch failed — mark explicitly so the UI falls back to a download chip
          setImagePreviews((prev) => ({ ...prev, [getAttachmentKey(att)]: null }));
        }
      }
    })();

    return () => {
      cancelled = true;
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [selectedEmail, getActiveAccount]);

  const fetchMessages = useCallback(async (selection) => {
    const account = accounts.find((a) => a.id === selection.accountId);
    if (!account) return;

    setListLoading(true);
    setMessages([]);
    setSelectedId(null);
    setSelectedEmail(null);

    try {
      if (account.type === 'gmail') {
        let rawList;
        if (selection.folder === 'DRAFT') {
          rawList = await EmailService.getDrafts(account.token, 20);
          rawList = rawList.map((d) => ({ id: d.message?.id || d.id, threadId: d.threadId }));
        } else {
          rawList = await EmailService.getMessages(account.token, FOLDER_LABEL_ID[selection.folder], 20);
        }

        if (!rawList || rawList.length === 0) { setMessages([]); return; }

        const metas = await Promise.all(
          rawList.map((m) => EmailService.getMessageMeta(account.token, m.id).catch(() => null))
        );
        const valid = metas.filter(Boolean);
        setMessages(valid);
        setLastSync(new Date());

        const unread = valid.filter((m) => m.isUnread).length;
        const key = `${account.id}:${selection.folder}`;
        setUnreadCounts((prev) => ({ ...prev, [key]: unread }));

      } else if (account.type === 'zoho') {
        const msgs = await EmailService.getZohoMessages(account.token, selection.folder, 20);
        setMessages(msgs);
        setLastSync(new Date());

        const unread = msgs.filter((m) => m.isUnread).length;
        const key = `${account.id}:${selection.folder}`;
        setUnreadCounts((prev) => ({ ...prev, [key]: unread }));

      } else {
        // SMTP — not yet supported for inbox reading
        setMessages([]);
      }
    } catch (err) {
      if (err?.response?.status === 401) onDisconnect(account.id);
    } finally {
      setListLoading(false);
    }
  }, [accounts, onDisconnect]);

  useEffect(() => {
    if (activeSelection) fetchMessages(activeSelection);
  }, [activeSelection, fetchMessages]);

  // When accounts list changes (e.g. new account added), ensure activeSelection is still valid
  useEffect(() => {
    if (!activeSelection || !accounts.find((a) => a.id === activeSelection.accountId)) {
      if (accounts.length > 0) {
        setActiveSelection({ accountId: accounts[0].id, folder: 'INBOX' });
      }
    }
  }, [accounts, activeSelection]);

  const handleFolderChange = (selection) => {
    setActiveSelection(selection);
    setActiveTab('inbox');
  };

  const handleSelectEmail = async (id) => {
    const account = getActiveAccount();
    if (!account || (account.type !== 'gmail' && account.type !== 'zoho')) return;

    setSelectedId(id);
    setSelectedEmail(null);
    setEmailLoading(true);
    setActiveTab('inbox');
    try {
      if (account.type === 'gmail') {
        const full = await EmailService.getMessage(account.token, id);
        setSelectedEmail(full);
      } else if (account.type === 'zoho') {
        const listMsg = messages.find((m) => m.id === id);
        const full = await EmailService.getZohoMessage(account.token, id, listMsg?.folderId);
        setSelectedEmail({
          ...full,
          subject: (full.subject && full.subject !== '(no subject)') ? full.subject : (listMsg?.subject || '(no subject)'),
          from: full.from?.email ? full.from : (listMsg?.from || full.from),
          date: full.date || listMsg?.date || '',
          snippet: full.snippet || listMsg?.snippet || '',
          isUnread: full.isUnread ?? listMsg?.isUnread ?? false,
        });
      }
    } catch (err) {
      if (err?.response?.status === 401) onDisconnect(account.id);
    } finally {
      setEmailLoading(false);
    }
  };

  const escapeHtml = (str) =>
    str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const buildQuotedBody = (msg) => {
    const dateStr = msg.date ? new Date(msg.date).toLocaleString() : '';
    const senderStr = msg.from?.name
      ? `${msg.from.name} <${msg.from.email}>`
      : (msg.from?.email || '');
    const original = msg.plain || msg.snippet || '';
    const originalHtml = escapeHtml(original).replace(/\n/g, '<br>');
    return `<br><br><blockquote>On ${escapeHtml(dateStr)}, ${escapeHtml(senderStr)} wrote:<br>${originalHtml}</blockquote>`;
  };

  const openCompose = (prefill = null) => {
    setComposePreFill(prefill);
    setActiveTab('compose');
  };

  const handleReply = (msg) => openCompose({
    title: 'Reply',
    to: msg.from?.email || '',
    subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`,
    body: buildQuotedBody(msg),
    fromAccountId: activeSelection?.accountId,
  });

  const handleReplyAll = (msg) => {
    const activeAccount = getActiveAccount();
    const senderEmail = msg.from?.email || '';
    const toAddresses = (msg.to || '')
      .split(',')
      .map((a) => a.trim())
      .filter((a) => a && a !== activeAccount?.email);
    const allTo = [senderEmail, ...toAddresses].filter(Boolean).join(', ');
    openCompose({
      title: 'Reply All',
      to: allTo,
      subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`,
      body: buildQuotedBody(msg),
      fromAccountId: activeSelection?.accountId,
    });
  };

  const handleForward = (msg) => openCompose({
    title: 'Forward',
    to: '',
    subject: msg.subject?.startsWith('Fwd:') ? msg.subject : `Fwd: ${msg.subject}`,
    body: buildQuotedBody(msg),
    fromAccountId: activeSelection?.accountId,
  });

  const handleDownloadAttachment = async (attachment) => {
    const account = getActiveAccount();
    if (!account || !selectedEmail) return;
    try {
      if (account.type === 'gmail') {
        await EmailService.downloadGmailAttachment(account.token, selectedEmail.id, attachment);
      } else if (account.type === 'zoho') {
        await EmailService.downloadZohoAttachment(account.token, selectedEmail.id, attachment);
      }
    } catch (err) {
      if (err?.response?.status === 401) onDisconnect(account.id);
    }
  };

  const handleDeleteEmail = async (email) => {
    const account = getActiveAccount();
    if (!account || account.type !== 'zoho') return;
    const msg = messages.find((m) => m.id === email.id);
    if (!msg?.folderId) return;
    try {
      await EmailService.deleteZohoMessage(account.token, email.id, msg.folderId);
      setMessages((prev) => prev.filter((m) => m.id !== email.id));
      if (selectedId === email.id) { setSelectedId(null); setSelectedEmail(null); }
    } catch (err) {
      if (err?.response?.status === 401) onDisconnect(account.id);
    }
  };

  const formatSyncTime = () => {
    if (!lastSync) return 'Not synced yet';
    const diff = Math.floor((Date.now() - lastSync) / 60000);
    if (diff < 1) return 'Just now';
    if (diff === 1) return '1 min ago';
    return `${diff} min ago`;
  };

  const activeAccount = getActiveAccount();
  const inboxUnread = messages.filter((m) => m.isUnread).length;
  const emailDisplay = activeAccount?.email || 'Connected';
  const providerLabel = activeAccount?.label || '';

  return (
    <div className="email-center-v2 ec-inbox-mode">
      {/* Header */}
      <div className="ec-header">
        <div className="ec-header-left">
          <div className="ec-header-icon"><FiMail size={15} /></div>
          <span>Email Center</span>

          <div className="ec-account-pill-wrap">
            <div
              className="ec-account-pill"
              onClick={() => setDropdownOpen((o) => !o)}
              style={{ marginLeft: 8 }}
            >
              <span className="ec-dot-green" />
              {emailDisplay}
              <span className="ec-provider-badge">{providerLabel}</span>
              <FiChevronDown size={12} style={{ marginLeft: 2 }} />
            </div>

            {dropdownOpen && (
              <AccountDropdown
                accounts={accounts}
                activeAccountId={activeSelection?.accountId}
                onSwitchAccount={(id) => {
                  setActiveSelection({ accountId: id, folder: 'INBOX' });
                  setDropdownOpen(false);
                }}
                onAddAccount={() => { setDropdownOpen(false); onAddAccount && onAddAccount(); }}
                onDisconnect={(id) => { setDropdownOpen(false); onDisconnect(id); }}
                onClose={() => setDropdownOpen(false)}
              />
            )}
          </div>
        </div>

        <div className="ec-header-right">
          <span className="ec-sync-text">
            <FiRefreshCw size={11} />
            Last sync: {formatSyncTime()}
          </span>
          <button className="ec-icon-btn" title="Settings">
            <FiSettings size={15} />
          </button>
        </div>
      </div>

      {/* Top tabs */}
      <div className="ec-top-tabs">
        <button
          className={`ec-tab${activeTab === 'inbox' ? ' active' : ''}`}
          onClick={() => setActiveTab('inbox')}
        >
          <FiMail size={13} />
          {activeSelection?.folder
            ? activeSelection.folder.charAt(0) + activeSelection.folder.slice(1).toLowerCase()
            : 'Inbox'}
          {activeSelection?.folder === 'INBOX' && inboxUnread > 0 && (
            <span className="ec-badge">{inboxUnread}</span>
          )}
        </button>
        <button
          className={`ec-tab${activeTab === 'compose' ? ' active' : ''}`}
          onClick={() => openCompose(null)}
        >
          <FiEdit size={13} />
          Compose
        </button>
      </div>

      {/* Body */}
      <div className="ec-body">
        <EmailSidebar
          accounts={accounts}
          activeSelection={activeSelection}
          onFolderChange={handleFolderChange}
          unreadCounts={unreadCounts}
          onRefresh={(accountId) => fetchMessages({ accountId, folder: activeSelection?.folder || 'INBOX' })}
        />

        <EmailList
          messages={messages}
          loading={listLoading}
          selectedId={selectedId}
          onSelect={handleSelectEmail}
          activeFolder={activeSelection?.folder || 'INBOX'}
        />

        {activeTab === 'compose' ? (
          <ComposePanel
            accounts={accounts}
            defaultFromAccountId={composePreFill?.fromAccountId || activeSelection?.accountId}
            preFill={composePreFill}
            onSent={() => {
              setComposePreFill(null);
              setActiveTab('inbox');
              if (activeSelection?.folder === 'SENT') fetchMessages(activeSelection);
            }}
            onCancel={() => {
              setComposePreFill(null);
              setActiveTab('inbox');
            }}
          />
        ) : (
          <EmailReadingPane
            email={selectedEmail}
            loading={emailLoading}
            onReply={handleReply}
            onReplyAll={handleReplyAll}
            onForward={handleForward}
            onDelete={handleDeleteEmail}
            onDownloadAttachment={handleDownloadAttachment}
            imagePreviews={imagePreviews}
          />
        )}
      </div>
    </div>
  );
};

export default EmailInboxLayout;
